import { useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Mic2, Radio } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useApi, errMsg } from "@/lib/api";
import { useMe } from "@/context/MeContext";

const field = "border-white/10 bg-[#181824] text-white";

const RoleCard = ({ id, icon: Icon, title, text, active, onClick }) => (
  <button data-testid={`onboard-role-${id}`} onClick={onClick} className={`rounded-xl border p-6 text-left transition-[border-color,background-color] ${active ? "border-[#FF4500] bg-[#FF4500]/10" : "border-white/10 bg-[#12121A] hover:border-white/30"}`}>
    <Icon className={`h-6 w-6 ${active ? "text-[#FF4500]" : "text-slate-400"}`} />
    <p className="mt-4 font-heading text-lg font-bold text-white">{title}</p>
    <p className="mt-1 text-sm text-slate-400">{text}</p>
  </button>
);

export default function Onboarding() {
  const { me, loading, refresh } = useMe();
  const api = useApi();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [role, setRole] = useState(params.get("rol") === "curator" ? "curator" : "artist");
  const [name, setName] = useState("");
  const [m, setM] = useState({ name: "", description: "", genres: "", country: "", format: "Radio online", cost_credits: 2 });
  const [busy, setBusy] = useState(false);

  if (loading) return <div className="h-[60vh]" />;
  if (!me) return <Navigate to="/sign-in" replace />;
  if (me.onboarded) return <Navigate to="/panel" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const media = role === "curator" ? { ...m, genres: m.genres.split(",").map((g) => g.trim()).filter(Boolean), cost_credits: Number(m.cost_credits) } : null;
      await api("post", "/me/onboard", { role, display_name: name, media });
      await refresh();
      navigate("/panel");
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} data-testid="onboarding-page" className="mx-auto max-w-2xl px-4 py-16">
      <p className="font-mono text-xs tracking-widest text-amber-500/90">BIENVENIDA</p>
      <h1 className="mt-4 font-heading text-4xl font-extrabold text-white sm:text-5xl">¿Cómo vas a usar RARA PERLA?</h1>
      {me.role !== "admin" && (
        <div className="mt-10 grid gap-4 sm:grid-cols-2">
          <RoleCard id="artist" icon={Mic2} title="Artista / Banda" text="Sube tu música, arma tu Press Kit y llega a la radio y a los medios." active={role === "artist"} onClick={() => setRole("artist")} />
          <RoleCard id="curator" icon={Radio} title="Curador / Medio" text="Aprueba canciones para la radio y recibe envíos pagados (70%)." active={role === "curator"} onClick={() => setRole("curator")} />
        </div>
      )}
      <label className="mt-8 block text-sm text-slate-300">{role === "curator" ? "Tu nombre" : "Nombre artístico"}</label>
      <Input data-testid="onboard-name-input" required value={name} onChange={(e) => setName(e.target.value)} className={`mt-2 ${field}`} />
      {role === "curator" && me.role !== "admin" && (
        <div className="mt-8 space-y-4 rounded-xl border border-white/10 bg-[#12121A] p-6">
          <p className="font-heading text-lg font-bold text-white">Tu medio</p>
          <Input data-testid="onboard-media-name" required placeholder="Nombre del medio" value={m.name} onChange={(e) => setM({ ...m, name: e.target.value })} className={field} />
          <Textarea data-testid="onboard-media-desc" placeholder="Descripción breve" value={m.description} onChange={(e) => setM({ ...m, description: e.target.value })} className={field} />
          <Input data-testid="onboard-media-genres" placeholder="Géneros (separados por comas)" value={m.genres} onChange={(e) => setM({ ...m, genres: e.target.value })} className={field} />
          <div className="grid gap-4 sm:grid-cols-3">
            <Input data-testid="onboard-media-country" placeholder="País" value={m.country} onChange={(e) => setM({ ...m, country: e.target.value })} className={field} />
            <Select value={m.format} onValueChange={(v) => setM({ ...m, format: v })}>
              <SelectTrigger data-testid="onboard-media-format" className={field}><SelectValue /></SelectTrigger>
              <SelectContent className="border-white/10 bg-[#181824] text-slate-200">{["Radio online", "FM", "Podcast", "Blog", "Playlist"].map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={String(m.cost_credits)} onValueChange={(v) => setM({ ...m, cost_credits: v })}>
              <SelectTrigger data-testid="onboard-media-cost" className={field}><SelectValue /></SelectTrigger>
              <SelectContent className="border-white/10 bg-[#181824] text-slate-200">{[1, 2, 3].map((c) => <SelectItem key={c} value={String(c)}>{c} crédito{c > 1 ? "s" : ""}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
      )}
      <button data-testid="onboard-submit-btn" disabled={busy} className="mt-10 rounded-full bg-[#FF4500] px-8 py-3 font-semibold text-white disabled:opacity-60">{busy ? "Guardando..." : "Continuar"}</button>
    </form>
  );
}
